import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, Loader2, AlertCircle } from 'lucide-react';
import { authApi } from '@/services/api.js';
import LanguageSwitcher from '@/components/common/LanguageSwitcher.jsx';

/**
 * Password-reset request page — phone- OR email-keyed.
 *
 * The BE forgot-password endpoint always returns 200 with a generic
 * "if a matching account exists…" message to avoid leaking account
 * existence. In dev/local it ALSO returns `data.dev_otp` so we can
 * thread the code through to the reset page.
 */
export default function ForgotPassword() {
  const navigate = useNavigate();

  const [identifier, setIdentifier] = useState('');
  const [submit,     setSubmit]     = useState(false);
  const [serverErr,  setServerErr]  = useState(null);

  async function onSubmit(ev) {
    ev.preventDefault();
    const id = identifier.trim();
    if (!id) {
      setServerErr('Enter your phone or email.');
      return;
    }
    setSubmit(true);
    setServerErr(null);
    try {
      const res = await authApi.forgotPassword(id);
      // Always proceed to /reset-password regardless of existence —
      // the BE will refuse the confirm step if the OTP isn't real.
      navigate('/reset-password', {
        replace: true,
        state: {
          identifier: id,
          devOtp:     res?.data?.dev_otp || null,
          message:    res?.message || null,
        },
      });
    } catch (err) {
      const status = err?.response?.status;
      const data   = err?.response?.data || {};
      if (status === 429) {
        setServerErr(data.message || 'Too many requests. Please try again later.');
      } else if (!err?.response) {
        setServerErr('Could not reach the NACIS API. Check your connection.');
      } else {
        setServerErr(data.message || 'Could not start the reset. Try again.');
      }
    } finally {
      setSubmit(false);
    }
  }

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <aside className="relative hidden bg-slate-950 lg:flex lg:flex-col lg:p-12 lg:text-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            background:
              'radial-gradient(900px circle at 0% 0%, rgba(56,189,248,0.18), transparent 45%), radial-gradient(800px circle at 100% 100%, rgba(14,165,233,0.18), transparent 45%)',
          }}
          aria-hidden
        />
        <div className="relative flex flex-1 flex-col">
          <Link to="/" className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-white/10">
              <Shield className="h-6 w-6" strokeWidth={2.4} />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-bold tracking-wide">NACIS</div>
              <div className="text-[10px] uppercase tracking-[0.14em] opacity-70">National AI Communications-Intelligence System</div>
            </div>
          </Link>

          <div className="mt-auto max-w-md">
            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-sky-300">Account recovery</div>
            <h2 className="mt-3 text-3xl font-semibold leading-tight">
              We'll send a one-time code to your registered channel.
            </h2>
            <p className="mt-4 text-sm leading-7 text-slate-300">
              Codes expire after 15 minutes. After resetting, all your existing
              sessions are revoked for safety — you'll need to sign in again on
              every device.
            </p>
          </div>
        </div>
      </aside>

      <main className="relative flex items-center justify-center px-6 py-12 lg:px-12">
        <div className="absolute right-4 top-4 lg:right-8 lg:top-8">
          <LanguageSwitcher variant="light" />
        </div>
        <div className="w-full max-w-sm">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Forgot your password?</h1>
          <p className="mt-1.5 text-sm text-slate-600">
            Enter your phone or email and we'll send you a 6-digit reset code.
          </p>

          {serverErr && (
            <div role="alert" className="mt-6 flex gap-2 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <div>{serverErr}</div>
            </div>
          )}

          <form onSubmit={onSubmit} className="mt-6 space-y-5" noValidate>
            <div>
              <label htmlFor="identifier" className="block text-sm font-medium text-slate-800">Phone or email</label>
              <input
                id="identifier"
                type="text"
                value={identifier}
                onChange={e => setIdentifier(e.target.value)}
                className="input mt-1"
                disabled={submit}
                autoComplete="email"
              />
            </div>

            <button type="submit" className="btn-primary w-full" disabled={submit}>
              {submit ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : 'Send reset code'}
            </button>
          </form>

          <div className="mt-6 text-xs text-slate-500">
            Remembered it?{' '}
            <Link to="/login" className="font-medium text-sky-700 hover:text-sky-800">
              Sign in
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
